import { useEffect, useMemo, useState, type ChangeEvent } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckCircle2, ChevronRight, CircleSlash2, Copy, Download, ExternalLink, FileCode2, FileSearch, FileText, FolderOpen, Globe2, History, Info, LoaderCircle, MoreHorizontal, Network as Sitemap, Plus, RefreshCw, Search, SearchCheck, Trash2, TriangleAlert, UploadCloud, X, type LucideIcon } from "lucide-react"
import { toast } from "sonner"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { AddDomainForm } from "@/components/editor/add-domain-form"
import { editorCardClass, editorDescriptionClass, editorHeadingClass } from "@/components/editor/editor-styles"
import { EmptyState } from "@/components/shared/empty-state"
import { StatTile } from "@/components/shared/stat-tile"
import { StatusBadge } from "@/components/shared/status-badge"
import { api } from "@/lib/api"
import { normalizeDomain } from "@/lib/domain"
import { queryKeys } from "@/lib/query-keys"
import type { ChatbotDetail, DiscoveryResult, DiscoveredPage, KnowledgeFile, SearchDomain, SearchResult } from "@/lib/types"
import { cn } from "@/lib/utils"

const GLOBAL_SCOPE = "global"
const PAGE_BATCH_SIZE = 40

function lastScan(domain: SearchDomain) {
  const latest = domain.pages.map(({ lastSeenAt }) => new Date(lastSeenAt).getTime()).filter(Number.isFinite).sort((a, b) => b - a)[0]
  return latest ? new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(latest) : "Nunca"
}
function pageCacheStatus(page: DiscoveredPage): { label: string; tone: "success" | "danger" | "warning" } | null {
  if (page.fetchError) {
    return { label: page.cachedContent ? "Cache expirado" : "Bloqueada", tone: page.cachedContent ? "warning" : "danger" }
  }
  if (page.cachedContent && page.cachedAt) {
    const ageHours = (Date.now() - new Date(page.cachedAt).getTime()) / (1000 * 60 * 60)
    const label = ageHours < 1 ? "Indexada agora" : ageHours < 24 ? `Indexada · ${Math.round(ageHours)}h` : `Indexada · ${Math.round(ageHours / 24)}d`
    return { label, tone: "success" }
  }
  return null
}

function DomainMetric({ label, value, tone }: { label: string; value: number; tone?: "success" | "danger" }) {
  return (
    <div className="flex min-h-[64px] flex-col justify-center px-4 py-3 first:pl-4 last:pr-4 sm:px-5">
      <p className="text-[10px] font-medium uppercase tracking-[.08em] text-muted-foreground">{label}</p>
      <p className={cn("mt-1 font-display text-xl font-bold leading-none tabular-nums", tone === "success" && "text-success", tone === "danger" && "text-destructive")}>{value}</p>
    </div>
  )
}

function BlockHeading({ id, icon: Icon, title, count, description }: { id?: string; icon: LucideIcon; title: string; count: number; description?: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground" aria-hidden="true">
          <Icon className="size-3.5" />
        </span>
        <div>
          <h3 id={id} className="text-xs font-semibold text-foreground">{title}</h3>
          {description && <p className="muted mt-0.5 text-[11px] leading-relaxed">{description}</p>}
        </div>
      </div>
      <Badge variant="secondary" className="shrink-0 text-[10px] font-semibold tabular-nums">{count}</Badge>
    </div>
  )
}

function RuleList({ type, rules, onChange, onSave }: { type: "ALLOW" | "BLOCK"; rules: string[]; onChange: (rules: string[]) => void; onSave: (rules: string[]) => void }) {
  const allow = type === "ALLOW"
  function remove(index: number) {
    const next = rules.filter((_, current) => current !== index)
    onChange(next); onSave(next)
  }
  return (
    <div className="rounded-lg border border-border panel-bg p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-md", allow ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive")}>
            {allow ? <CheckCircle2 className="size-3.5" /> : <CircleSlash2 className="size-3.5" />}
          </span>
          <div>
            <p className="text-xs font-bold">{allow ? "Caminhos permitidos" : "Caminhos bloqueados"}</p>
            <p className="mt-0.5 text-[11px] muted">{allow ? "O assistente pode consultar os caminhos correspondentes." : "Áreas sensíveis ficam fora da busca."}</p>
          </div>
        </div>
        <StatusBadge tone={allow ? "success" : "danger"} dot={false}>{allow ? "Permitido" : "Bloqueado"}</StatusBadge>
      </div>
      <div className="space-y-2">
        {rules.map((rule, index) => (
          <div key={`${type}-${index}`} className="flex gap-2">
            <Input aria-label={`${allow ? "Página permitida" : "Página bloqueada"} ${index + 1}`} className="font-mono text-xs h-8 rounded-md" value={rule} onChange={(event) => onChange(rules.map((current, currentIndex) => currentIndex === index ? event.target.value : current))} onBlur={() => onSave(rules)} placeholder={allow ? "/produtos/*" : "/login/*"} />
            <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0 text-muted-foreground hover:text-destructive" disabled={allow && rules.length === 1} onClick={() => remove(index)}>
              <X className="size-3.5" />
              <span className="sr-only">Remover caminho</span>
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-3 h-7 text-xs" onClick={() => onChange([...rules, ""])}>
        <Plus className="mr-1.5 size-3" />Adicionar caminho
      </Button>
    </div>
  )
}

function DomainWorkspace({ chatbotId, domain, onRefresh, embedded = false }: { chatbotId: string; domain: SearchDomain; onRefresh: (detail: ChatbotDetail) => void; embedded?: boolean }) {
  const [view, setView] = useState("pages")
  const [discovering, setDiscovering] = useState(false)
  const [savingRules, setSavingRules] = useState(false)
  const [pageFilter, setPageFilter] = useState("")
  const [pageLimit, setPageLimit] = useState(PAGE_BATCH_SIZE)
  const [allow, setAllow] = useState<string[]>([])
  const [block, setBlock] = useState<string[]>([])
  const [pendingPage, setPendingPage] = useState<string>()
  const [lastProbe, setLastProbe] = useState<DiscoveryResult | null>(null)

  useEffect(() => {
    setAllow(domain.rules.filter((rule) => rule.type === "ALLOW").map((rule) => rule.pattern))
    setBlock(domain.rules.filter((rule) => rule.type === "BLOCK").map((rule) => rule.pattern))
  }, [domain.id, domain.rules])

  useEffect(() => {
    setView("pages")
    setPageFilter("")
    setPageLimit(PAGE_BATCH_SIZE)
    setLastProbe(null)
  }, [domain.id])

  const visiblePages = useMemo(() => domain.pages.filter((page) => `${page.title} ${page.path}`.toLowerCase().includes(pageFilter.toLowerCase())), [domain.pages, pageFilter])
  const pagesToRender = visiblePages.slice(0, pageLimit)
  const selectedCount = useMemo(() => domain.pages.filter(({ selected }) => selected).length, [domain.pages])
  const wholeSite = allow.includes("/*")

  async function discover() {
    setDiscovering(true)
    try {
      const result = await api.discoverPages(chatbotId, domain.id)
      setLastProbe(result)
      onRefresh(result.chatbot)
      const okSitemaps = result.sitemaps.filter((s) => s.ok).length
      const blockedSitemaps = result.sitemaps.filter((s) => !s.ok).length
      if (blockedSitemaps > 0 && okSitemaps === 0) {
        toast.warning(`Não conseguimos acessar o sitemap deste site — provável proteção anti-bot. Use URLs manuais abaixo.`)
      } else if (result.pages.length && result.sitemapFound) toast.success(`${result.pages.length} páginas encontradas no site.`)
      else if (result.pages.length) toast.success("A página inicial foi adicionada ao conhecimento; este site não publica sitemap.")
      else toast.warning("Não encontramos páginas neste domínio.")
    } catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível buscar as páginas.") }
    finally { setDiscovering(false) }
  }

  async function toggle(pageId: string, selected: boolean) {
    setPendingPage(pageId)
    try { onRefresh(await api.togglePage(chatbotId, pageId, selected)) }
    catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível alterar esta página.") }
    finally { setPendingPage(undefined) }
  }

  async function persistRules(nextAllow = allow, nextBlock = block, successMessage?: string) {
    const cleanAllow = nextAllow.map((item) => item.trim()).filter(Boolean)
    const cleanBlock = nextBlock.map((item) => item.trim()).filter(Boolean)
    if (!cleanAllow.length) return toast.error("Mantenha pelo menos um caminho permitido.")
    setSavingRules(true)
    try { onRefresh(await api.updateRules(chatbotId, domain.id, cleanAllow, cleanBlock)); if (successMessage) toast.success(successMessage) }
    catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível salvar o escopo.") }
    finally { setSavingRules(false) }
  }

  const [manualText, setManualText] = useState("")
  const [savingManual, setSavingManual] = useState(false)
  useEffect(() => { setManualText((domain.manualUrls ?? []).join("\n")) }, [domain.manualUrls])

  async function saveManualUrls() {
    const lines = manualText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
    const invalid = lines.filter((line) => { try { const u = new URL(line); return !["http:", "https:"].includes(u.protocol) || u.hostname.toLowerCase() !== domain.hostname } catch { return true } })
    if (invalid.length) return toast.error(`URLs inválidas ou fora de ${domain.hostname}: ${invalid.slice(0, 3).join(", ")}`)
    setSavingManual(true)
    try {
      const detail = await api.updateManualUrls(chatbotId, domain.id, lines)
      onRefresh(detail)
      const result = await api.discoverPages(chatbotId, domain.id)
      setLastProbe(result)
      onRefresh(result.chatbot)
      toast.success(`${lines.length} URLs manuais salvas e indexadas.`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar as URLs manuais.")
    } finally { setSavingManual(false) }
  }

  async function setScope(mode: "whole" | "specific") {
    const nextAllow = mode === "whole"
      ? ["/*"]
      : domain.pages.filter(({ selected }) => selected).map(({ path }) => path).slice(0, 100)
    const safeAllow = nextAllow.length ? nextAllow : ["/"]
    setAllow(safeAllow)
    await persistRules(safeAllow, block, mode === "whole" ? "Busca liberada para o site inteiro, respeitando bloqueios." : "Busca limitada às páginas selecionadas.")
  }

  return (
    <div className={cn("overflow-hidden", embedded ? "rounded-xl border border-border" : "panel-bg rounded-2xl border border-border")}>
      <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
            <Globe2 className="size-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">{domain.hostname}</p>
            <p className="text-[11px] muted">Rastreamento do site · Última descoberta: {lastScan(domain)}</p>
          </div>
        </div>
        <Button onClick={() => void discover()} disabled={discovering} size="sm" className="h-8 rounded-lg text-xs font-semibold shrink-0">
          {discovering ? <LoaderCircle className="mr-1.5 size-3.5 animate-spin" /> : <Sitemap className="mr-1.5 size-3.5" />}
          {discovering ? "Descobrindo..." : "Descobrir páginas"}
        </Button>
      </div>

      <div className="grid grid-cols-3 divide-x divide-border border-b border-border bg-muted/40">
        <DomainMetric label="Descobertas" value={domain.pages.length} />
        <DomainMetric label="Permitidas" value={selectedCount} tone="success" />
        <DomainMetric label="Bloqueadas" value={domain.pages.length - selectedCount} tone="danger" />
      </div>

      <div className="p-4 sm:p-5">
        <div className="mb-4">
          <p className="text-xs font-bold text-foreground">Onde a IA pode buscar?</p>
          <p className="mt-0.5 text-[11px] muted">Escolha um escopo inicial. Caminhos bloqueados específicos continuam excluídos.</p>
          <div className="mt-2.5 grid grid-cols-1 gap-2.5 lg:grid-cols-2">
            <Button type="button" variant={wholeSite ? "default" : "outline"} className="h-auto min-w-0 justify-start overflow-hidden rounded-lg p-3 text-left whitespace-normal" disabled={savingRules} onClick={() => void setScope("whole")}>
              <span className="mr-2.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"><Globe2 className="size-3.5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-xs font-bold">Site inteiro</span><span className="block whitespace-normal text-[10px] font-normal leading-4 opacity-75">Permite todas as páginas, exceto os caminhos bloqueados</span></span>
            </Button>
            <Button type="button" variant={!wholeSite ? "default" : "outline"} className="h-auto min-w-0 justify-start overflow-hidden rounded-lg p-3 text-left whitespace-normal" disabled={savingRules || !domain.pages.length} onClick={() => void setScope("specific")}>
              <span className="mr-2.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-success/10 text-success"><CheckCircle2 className="size-3.5" /></span>
              <span className="min-w-0 flex-1"><span className="block text-xs font-bold">Páginas específicas</span><span className="block whitespace-normal text-[10px] font-normal leading-4 opacity-75">Usa apenas as páginas selecionadas abaixo</span></span>
            </Button>
          </div>
        </div>

        <Tabs value={view} onValueChange={setView}>
          <TabsList className="mb-4 h-8 rounded-lg border border-border bg-muted/40 p-0.5">
            <TabsTrigger value="pages" className="rounded-md px-2.5 text-xs font-semibold">Páginas descobertas</TabsTrigger>
            <TabsTrigger value="manual" className="rounded-md px-2.5 text-xs font-semibold">URLs manuais</TabsTrigger>
            <TabsTrigger value="advanced" className="rounded-md px-2.5 text-xs font-semibold">Regras avançadas</TabsTrigger>
          </TabsList>

          <TabsContent value="pages" className="mt-0">
            <div className="mb-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
              <div><p className="text-xs font-bold text-foreground">Gerenciar páginas</p><p className="mt-0.5 text-[11px] muted">Selecione apenas o conteúdo que o assistente pode usar.</p></div>
              {domain.pages.length > 0 && (
                <div className="relative w-full sm:w-52">
                  <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-8 h-7 text-xs rounded-lg" placeholder="Buscar páginas" value={pageFilter} onChange={(event) => setPageFilter(event.target.value)} />
                </div>
              )}
            </div>
            {domain.pages.length ? (
              <ScrollArea className="h-[320px] rounded-lg border border-border">
                <div className="divide-y divide-border">
                  {pagesToRender.map((page) => (
                    <label key={page.id} className={cn("flex cursor-pointer items-center gap-2.5 px-3 py-2 text-xs transition-colors duration-200 hover:bg-muted/40", pendingPage === page.id && "opacity-60")}>
                      <Checkbox checked={page.selected} disabled={pendingPage === page.id} onCheckedChange={(checked) => void toggle(page.id, checked === true)} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-semibold text-foreground">{page.title}</p>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <StatusBadge tone={page.selected ? "success" : "danger"} dot={false}>{page.selected ? "Permitida" : "Bloqueada"}</StatusBadge>
                            {(() => { const cache = pageCacheStatus(page); return cache ? <StatusBadge tone={cache.tone} dot>{cache.label}</StatusBadge> : null })()}
                          </div>
                        </div>
                        <p className="mt-0.5 truncate font-mono text-[11px] muted">{page.path}</p>
                      </div>
                      {pendingPage === page.id ? (
                        <LoaderCircle className="size-3.5 animate-spin text-muted-foreground" />
                      ) : (
                        <a href={page.url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-card hover:text-primary">
                          <ExternalLink className="size-3.5" />
                          <span className="sr-only">Abrir página</span>
                        </a>
                      )}
                    </label>
                  ))}
                </div>
                {visiblePages.length > pageLimit && (
                  <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2.5">
                    <p className="muted text-[10px]">Mostrando {pagesToRender.length} de {visiblePages.length} páginas</p>
                    <Button type="button" variant="outline" size="sm" className="h-7 rounded-md px-2.5 text-[10px] font-semibold" onClick={() => setPageLimit((current) => current + PAGE_BATCH_SIZE)}>
                      Mostrar mais
                    </Button>
                  </div>
                )}
                {!visiblePages.length && <EmptyState size="sm" icon={Search} title="Nenhuma página encontrada" description="Tente outro termo de busca." className="h-28 border-none" />}
              </ScrollArea>
            ) : (
              <EmptyState icon={Sitemap} title="Descubra as páginas deste site" description="Vamos inspecionar o sitemap e mostrar páginas para você escolher." action={<Button size="sm" className="mt-2 h-7 rounded-lg text-xs" onClick={() => void discover()} disabled={discovering}>{discovering ? <LoaderCircle className="mr-1.5 size-3.5 animate-spin" /> : <Sitemap className="mr-1.5 size-3.5" />}{discovering ? "Descobrindo..." : "Descobrir páginas"}</Button>} />
            )}
          </TabsContent>

          <TabsContent value="manual" className="mt-0">
            <div className="mb-2"><p className="text-xs font-bold text-foreground">URLs manuais</p><p className="mt-0.5 text-[11px] muted">Adicione uma URL por linha para <span className="font-mono font-semibold">{domain.hostname}</span>.</p></div>
            {lastProbe && lastProbe.sitemaps.length > 0 && lastProbe.sitemaps.every((s) => !s.ok) ? (
              <Alert className="mb-3 border-warning/30 bg-warning/10 p-2.5 text-warning">
                <TriangleAlert className="size-3.5 text-warning" />
                <AlertTitle className="text-xs font-bold text-warning">Sitemap bloqueado por proteção antibot</AlertTitle>
                <AlertDescription className="text-[11px] text-warning/85">{lastProbe.sitemaps[0]?.reason ?? "A proteção antibot está ativa"}. Adicione URLs manuais abaixo.</AlertDescription>
              </Alert>
            ) : null}
            <Textarea rows={5} value={manualText} onChange={(event) => setManualText(event.target.value)} placeholder={"https://" + domain.hostname + "/sobre" + "\n" + "https://" + domain.hostname + "/produtos"} className="font-mono text-xs rounded-lg p-2.5" />
            <div className="mt-2.5 flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-1.5">
              <p className="text-[11px] muted">{manualText.split(/\r?\n/).filter((line) => line.trim()).length} URLs prontas para salvar.</p>
              <Button size="sm" className="h-7 rounded-md text-xs font-semibold" onClick={() => void saveManualUrls()} disabled={savingManual}>
                {savingManual && <LoaderCircle className="mr-1.5 size-3.5 animate-spin" />}Salvar e indexar
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="mt-0">
            <Alert className="mb-3 border-info/30 bg-info/10 p-2.5 text-info">
              <Info className="size-3.5 text-info" />
              <AlertTitle className="text-xs font-bold text-info">Controles avançados de rotas</AlertTitle>
              <AlertDescription className="text-[11px] text-info/85">Use <code className="rounded bg-info/15 px-1 text-info">*</code> como curinga.</AlertDescription>
            </Alert>
            <div className="grid gap-3 lg:grid-cols-2">
              <RuleList type="ALLOW" rules={allow} onChange={setAllow} onSave={(next) => void persistRules(next, block)} />
              <RuleList type="BLOCK" rules={block} onChange={setBlock} onSave={(next) => void persistRules(allow, next)} />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-lg border border-border bg-muted/40 px-3 py-1.5">
              <p className="text-[11px] muted">Salvo ao sair do campo.</p>
              <Button size="sm" className="h-7 rounded-md text-xs font-semibold" onClick={() => void persistRules(allow, block, "Regras atualizadas.")} disabled={savingRules}>
                {savingRules && <LoaderCircle className="mr-1.5 size-3.5 animate-spin" />}Salvar regras
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

function formatFileSize(size: number) {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(name: string) {
  return /\.(md|markdown)$/i.test(name) ? { Icon: FileCode2, tone: "bg-muted text-muted-foreground" } : { Icon: FileText, tone: "bg-info/10 text-info" }
}

function formatExtractedTextLength(length: unknown) {
  const numericLength = typeof length === "number" ? length : Number(length)
  return Number.isFinite(numericLength) ? numericLength.toLocaleString("pt-BR") : "—"
}

function filesInScope(files: unknown, domainId: string | null): KnowledgeFile[] {
  if (!Array.isArray(files)) return []
  return files.filter((file): file is KnowledgeFile => {
    if (!file || typeof file !== "object") return false
    const candidate = file as Partial<KnowledgeFile>
    return typeof candidate.id === "string" && (candidate.domainId ?? null) === domainId
  })
}

// Blindagem de coleções: o render deste painel acessa direto
// `detail.files.filter`, `detail.installDomains.length`, `detail.textSearchCounts[...]`
// e as listas aninhadas do domínio. Se o servidor devolver um payload degradado,
// o acesso cru estoura TypeError e desmonta a árvore. Normalizamos uma vez aqui
// para que todo acesso abaixo seja seguro.
function withCollections(detail: ChatbotDetail): ChatbotDetail {
  return {
    ...detail,
    suggestedQuestions: Array.isArray(detail.suggestedQuestions) ? detail.suggestedQuestions : [],
    quickActions: Array.isArray(detail.quickActions) ? detail.quickActions : [],
    aiModels: Array.isArray(detail.aiModels) ? detail.aiModels : [],
    installDomains: Array.isArray(detail.installDomains) ? detail.installDomains : [],
    files: Array.isArray(detail.files) ? detail.files : [],
    textSearches: Array.isArray(detail.textSearches) ? detail.textSearches : [],
    textSearchCounts: detail.textSearchCounts && typeof detail.textSearchCounts === "object" ? detail.textSearchCounts : {},
    domains: Array.isArray(detail.domains)
      ? detail.domains.map((domain) => ({
        ...domain,
        manualUrls: Array.isArray(domain.manualUrls) ? domain.manualUrls : [],
        rules: Array.isArray(domain.rules) ? domain.rules : [],
        pages: Array.isArray(domain.pages) ? domain.pages : [],
        files: Array.isArray(domain.files) ? domain.files : [],
        textSearches: Array.isArray(domain.textSearches) ? domain.textSearches : [],
      }))
      : [],
  }
}

function ScopeSelect({ domains, value, onChange, id, label }: { domains: SearchDomain[]; value: string; onChange: (next: string) => void; id: string; label: string }) {
  return (
    <div className="w-full sm:w-64">
      <label htmlFor={id} className="mb-1 block text-[11px] font-semibold muted">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} className="field-bg h-9 rounded-lg border-border text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={GLOBAL_SCOPE}>Global · todos os domínios</SelectItem>
          {domains.map((domain) => <SelectItem key={domain.id} value={domain.id}>{domain.hostname}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

function UploadZone({ chatbotId, domainId, onRefresh }: { chatbotId: string; domainId: string | null; onRefresh: (detail: ChatbotDetail) => void }) {
  const [uploadError, setUploadError] = useState<string>()
  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadFile(chatbotId, file, domainId),
  })
  const uploading = uploadMutation.isPending

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const input = event.currentTarget
    const file = event.target.files?.[0]
    if (!file) return
    setUploadError(undefined)
    try {
      onRefresh(await uploadMutation.mutateAsync(file))
      toast.success(domainId ? "Arquivo adicionado a este domínio." : "Arquivo global adicionado ao conhecimento.")
    } catch (error) {
      const message = error instanceof Error ? error.message : "Não foi possível processar o arquivo."
      setUploadError(message)
      toast.error(message)
    } finally {
      input.value = ""
    }
  }

  return (
    <div>
      <label className={cn("flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/20 px-5 py-7 text-center transition-colors duration-200 hover:border-foreground/20 hover:bg-muted/40", uploading && "pointer-events-none opacity-60")}>
        <input type="file" className="sr-only" accept=".pdf,.txt,.md,.markdown,application/pdf,text/plain,text/markdown" onChange={(event) => void upload(event)} disabled={uploading} />
        <span className="flex size-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          {uploading ? <LoaderCircle className="size-4 animate-spin" /> : <UploadCloud className="size-4" />}
        </span>
        <span className="mt-3 text-[13px] font-semibold">{domainId ? "Enviar arquivo para este domínio" : "Enviar arquivo global"}</span>
        <span className="mt-1 text-[11px] muted">PDF, TXT ou Markdown · Máximo de 20 MB por arquivo</span>
      </label>
      {uploadError && <p role="alert" className="mt-2 text-xs text-destructive">{uploadError}</p>}
    </div>
  )
}

function FileSection({ chatbotId, detail, domains, scope, onScopeChange, onRefresh }: { chatbotId: string; detail: ChatbotDetail; domains: SearchDomain[]; scope: string; onScopeChange: (next: string) => void; onRefresh: (detail: ChatbotDetail) => void }) {
  const scopeDomainId = scope === GLOBAL_SCOPE ? null : scope
  const queryClient = useQueryClient()
  const fallbackFiles = useMemo(() => filesInScope(detail.files, scopeDomainId), [detail.files, scopeDomainId])
  const filesQuery = useQuery({
    queryKey: queryKeys.domainFiles(chatbotId, scopeDomainId),
    queryFn: () => scopeDomainId ? api.listDomainFiles(chatbotId, scopeDomainId) : Promise.resolve(fallbackFiles),
    initialData: fallbackFiles,
  })
  const files = filesQuery.data ?? fallbackFiles
  const [moving, setMoving] = useState<string>()
  const [deleting, setDeleting] = useState<string>()
  const [publishing, setPublishing] = useState<string>()

  function syncFileQueries(next: ChatbotDetail) {
    queryClient.setQueryData(queryKeys.domainFiles(chatbotId, null), filesInScope(next.files, null))
    for (const domain of domains) queryClient.setQueryData(queryKeys.domainFiles(chatbotId, domain.id), filesInScope(next.files, domain.id))
    onRefresh(next)
  }

  function refreshAfterUpload(next: ChatbotDetail) {
    syncFileQueries(next)
  }

  const moveMutation = useMutation({
    mutationFn: ({ fileId, nextDomainId }: { fileId: string; nextDomainId: string | null }) => api.moveFile(chatbotId, fileId, nextDomainId),
  })

  const removeMutation = useMutation({
    mutationFn: async (fileId: string) => {
      await api.deleteFile(chatbotId, fileId)
      return api.getChatbot(chatbotId)
    },
  })

  async function move(file: KnowledgeFile, nextDomainId: string | null) {
    if ((file.domainId ?? null) === nextDomainId) return
    setMoving(file.id)
    try {
      syncFileQueries(await moveMutation.mutateAsync({ fileId: file.id, nextDomainId }))
      toast.success(nextDomainId ? "Arquivo movido para o domínio." : "Arquivo movido para Global.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível mover o arquivo.")
    } finally { setMoving(undefined) }
  }

  async function remove(file: KnowledgeFile) {
    setDeleting(file.id)
    try {
      syncFileQueries(await removeMutation.mutateAsync(file.id))
      toast.success("Arquivo removido.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível remover o arquivo.")
    } finally { setDeleting(undefined) }
  }

  async function changeVisibility(file: KnowledgeFile, visibility: "internal" | "public") {
    setPublishing(file.id)
    try {
      syncFileQueries(await api.setFileVisibility(chatbotId, file.id, visibility))
      toast.success(visibility === "public" ? "Arquivo liberado para respostas no chat público." : "Arquivo restrito ao teste interno.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível alterar a visibilidade.")
    } finally { setPublishing(undefined) }
  }

  const totalSize = files.reduce((sum, file) => sum + file.size, 0)

  return (
    <section className="panel-bg overflow-hidden rounded-2xl border border-border">
      <div className="border-b border-border p-6 sm:p-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div className="flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground"><FolderOpen className="size-[18px]" /></span>
            <div>
              <h2 className={editorHeadingClass}>Arquivos de conhecimento</h2>
              <p className={editorDescriptionClass}>Arquivos são internos por padrão, disponíveis apenas no teste autenticado. Escolha Público para liberar seu conteúdo nas respostas aos visitantes.</p>
            </div>
          </div>
          <span className="text-[11px] muted">{files.length} arquivos · {formatFileSize(totalSize)}</span>
        </div>
        <div className="mt-5">
          <ScopeSelect id="files-scope" label="Domínio do arquivo" domains={domains} value={scope} onChange={onScopeChange} />
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <UploadZone chatbotId={chatbotId} domainId={scopeDomainId} onRefresh={refreshAfterUpload} />

        {filesQuery.error && <p role="alert" className="mt-2 text-xs text-destructive">{filesQuery.error instanceof Error ? filesQuery.error.message : "Não foi possível carregar os arquivos."}</p>}

        <div className="mt-5 divide-y divide-border overflow-hidden rounded-xl border border-border">
          {files.length ? files.map((file) => {
            const { Icon, tone } = fileIcon(file.originalName)
            return (
              <div key={file.id} className="flex flex-col gap-2.5 p-2.5 text-xs sm:flex-row sm:items-center">
                <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-md", tone)}><Icon className="size-3.5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-foreground">{file.originalName}</p>
                  <p className="truncate text-[10px] muted">{formatFileSize(file.size)} · {formatExtractedTextLength(file.extractedTextLength)} caracteres · {file.domainId ? "Domínio" : "Global"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={file.visibility ?? "internal"} onValueChange={(value) => void changeVisibility(file, value as "internal" | "public")} disabled={publishing === file.id}>
                    <SelectTrigger className="h-8 w-28 text-[11px]" aria-label={`Visibilidade de ${file.originalName}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="internal">Interno</SelectItem><SelectItem value="public">Público</SelectItem></SelectContent>
                  </Select>
                  <Select value={file.domainId ?? GLOBAL_SCOPE} onValueChange={(next) => void move(file, next === GLOBAL_SCOPE ? null : next)}>
                    <SelectTrigger className="field-bg h-8 w-44 rounded-md border-border text-[11px]" disabled={moving === file.id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={GLOBAL_SCOPE}>Global</SelectItem>
                      {domains.map((domain) => <SelectItem key={domain.id} value={domain.id}>{domain.hostname}</SelectItem>)}
                    </SelectContent>
                  </Select>
                    <a href={`/api/chatbots/${chatbotId}/files/${file.id}/download`} className="muted flex size-7 items-center justify-center rounded hover:text-foreground" aria-label={`Baixar ${file.originalName}`}>
                    <Download className="size-3.5" />
                  </a>
                    <Button type="button" variant="ghost" size="icon" className="size-7 muted hover:text-destructive" disabled={deleting === file.id} onClick={() => void remove(file)} aria-label={`Remover ${file.originalName}`}>
                    {deleting === file.id ? <LoaderCircle className="size-3 animate-spin" /> : <Trash2 className="size-3" />}
                  </Button>
                </div>
              </div>
            )
          }) : (
            <div className="p-4 text-center">
              <p className="text-xs font-semibold">Nenhum arquivo {scopeDomainId ? "neste domínio" : "global"} ainda</p>
              <p className="mt-0.5 text-[10px] muted">O texto enviado será indexado para respostas relevantes.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function SearchSection({ chatbotId, domains, scope, onScopeChange }: { chatbotId: string; domains: SearchDomain[]; scope: string; onScopeChange: (next: string) => void }) {
  const [query, setQuery] = useState("Quais são os planos disponíveis?")
  const [results, setResults] = useState<SearchResult[] | null>(null)
  const queryClient = useQueryClient()
  const scopeDomainId = scope === GLOBAL_SCOPE ? undefined : scope
  const historyQueryKey = queryKeys.textSearches(chatbotId, scopeDomainId)
  const historyQuery = useQuery({
    queryKey: historyQueryKey,
    queryFn: () => api.listTextSearches(chatbotId, scopeDomainId),
    enabled: Boolean(chatbotId),
  })
  const history = historyQuery.data ?? []
  const searchMutation = useMutation({
    mutationFn: () => api.testSearch(chatbotId, query, scopeDomainId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: historyQueryKey })
    },
  })

  useEffect(() => { setResults(null) }, [scope])

  async function search() {
    if (query.trim().length < 2) return
    try {
      const response = await searchMutation.mutateAsync()
      setResults(response.results)
    } catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível testar a busca.") }
  }

  return (
    <section className="panel-bg overflow-hidden rounded-2xl border border-border">
      <div className="border-b border-border p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground"><SearchCheck className="size-[18px]" /></span>
          <div>
            <h2 className={editorHeadingClass}>Buscas de texto</h2>
            <p className={editorDescriptionClass}>Teste o que o assistente encontra e revise o histórico por domínio.</p>
          </div>
        </div>
        <div className="mt-5">
          <ScopeSelect id="search-scope" label="Domínio da busca" domains={domains} value={scope} onChange={onScopeChange} />
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <div className={editorCardClass}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void search() }} disabled={searchMutation.isPending} placeholder="ex.: Como faço para mudar de plano?" className="field-bg h-12 rounded-xl border-border px-4 text-[13px]" />
            <Button onClick={() => void search()} disabled={searchMutation.isPending} size="sm" className="accent-bg h-12 shrink-0 rounded-xl px-5 text-[13px] font-semibold">
              {searchMutation.isPending ? <LoaderCircle className="mr-1.5 size-3.5 animate-spin" /> : <Search className="mr-1.5 size-3.5" />}Executar busca
            </Button>
          </div>
          {results ? (
        <div className="mt-4 overflow-hidden rounded-lg border border-border">
              <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
                <span className="text-xs font-bold">Fontes encontradas</span>
                <Badge variant="secondary" className="text-[10px]">{results.length}</Badge>
              </div>
              {results.length ? (
                <div className="divide-y divide-border">
                  {results.map((result) => (
                    <a key={result.url} href={result.url} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 px-3 py-2.5 transition-colors duration-200 hover:bg-muted/30">
                      <FileSearch className="size-3.5 text-primary shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold">{result.title}</p>
                        <p className="truncate text-[10px] muted">{result.url}</p>
                      </div>
                      <StatusBadge tone="success" dot={false}>Permitida</StatusBadge>
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              ) : (
                <EmptyState size="sm" icon={CircleSlash2} title="Nenhuma fonte autorizada correspondeu a esta busca" description="Revise as páginas selecionadas e os caminhos permitidos do domínio." className="border-none py-6" />
              )}
            </div>
          ) : (
            <EmptyState size="sm" icon={Search} title="Digite uma pergunta para testar o conhecimento conectado" description="Veja quais fontes conectadas o assistente usaria para responder." className="mt-4 border-none py-6" />
          )}
        </div>

        <div className="mt-5 overflow-hidden rounded-xl border border-border">
          <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2">
            <span className="flex items-center gap-2 text-xs font-bold"><History className="size-3.5 muted" />Histórico de buscas</span>
            <Badge variant="secondary" className="text-[10px]">{history.length}</Badge>
          </div>
          {historyQuery.isPending ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
            </div>
          ) : historyQuery.error ? (
            <div role="alert" className="p-4 text-center">
              <p className="text-xs font-semibold">Não foi possível carregar o histórico</p>
              <p className="mt-0.5 text-[10px] text-destructive">{historyQuery.error instanceof Error ? historyQuery.error.message : "Tente novamente."}</p>
              <Button type="button" variant="outline" size="sm" className="mt-2 h-7 rounded-md text-[10px]" onClick={() => void historyQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : history.length ? (
            <div className="divide-y divide-border">
              {history.map((entry) => (
                <div key={entry.id} className="flex items-center gap-2.5 px-3 py-2 text-xs">
                  <FileSearch className="size-3.5 shrink-0 text-info" />
                  <p className="min-w-0 flex-1 truncate font-semibold text-foreground">{entry.query}</p>
                  <span className="muted shrink-0 text-[10px]">{entry.resultCount} fonte(s)</span>
                  <span className="muted shrink-0 text-[10px]">{entry.domainId ? domains.find((domain) => domain.id === entry.domainId)?.hostname ?? "Domínio" : "Global"}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center">
              <p className="text-xs font-semibold">Nenhuma busca registrada ainda</p>
              <p className="mt-0.5 text-[10px] muted">Execute uma busca de texto para começar o histórico.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

function DomainMenu({ domain, refreshing, onRefresh, onCopy, onRemove }: { domain: SearchDomain; refreshing: boolean; onRefresh: () => void; onCopy: () => void; onRemove: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0 rounded-lg text-muted-foreground hover:text-foreground" aria-label={`Ações de ${domain.hostname}`}>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="truncate text-[11px] font-semibold text-muted-foreground">{domain.hostname}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={refreshing} onSelect={onRefresh} className="gap-2 text-xs">
          {refreshing ? <LoaderCircle className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}Atualizar domínio
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onCopy} className="gap-2 text-xs">
          <Copy className="size-3.5" />Copiar endereço
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onRemove} className="gap-2 text-xs text-destructive focus:bg-destructive/10 focus:text-destructive">
          <Trash2 className="size-3.5" />Remover domínio
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function GlobalBucketCard({ detail }: { detail: ChatbotDetail }) {
  const files = detail.files.filter((file) => !file.domainId)
  const searches = detail.textSearches.filter((search) => !search.domainId)
  const searchCount = detail.textSearchCounts[GLOBAL_SCOPE] ?? searches.length
  const visibleFiles = files.slice(0, 4)
  const visibleSearches = searches.slice(0, 4)
  const totalSize = files.reduce((sum, file) => sum + file.size, 0)

  return (
    <Card className="border-primary/25 bg-primary/[.035] shadow-none">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary" aria-hidden="true">
              <FolderOpen className="size-[18px]" />
            </span>
            <div>
              <CardTitle className="text-sm font-semibold">Global · todos os domínios</CardTitle>
              <p className="muted mt-1 text-[11px] leading-relaxed">Conteúdo sem vínculo de domínio, disponível para qualquer página.</p>
            </div>
          </div>
          <Badge variant="outline" className="shrink-0 self-start border-primary/30 text-[10px] font-semibold text-primary sm:self-auto">Sem domínio</Badge>
        </div>
      </CardHeader>
      <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-3.5">
            <BlockHeading icon={FileText} title="Arquivos globais" count={files.length} />
            {visibleFiles.length ? (
              <ul className="mt-3 space-y-1.5">
                {visibleFiles.map((file) => (
                  <li key={file.id} className="flex items-center gap-2 text-xs">
                    <FileText className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{file.originalName}</span>
                    <span className="muted shrink-0 text-[10px]">{formatFileSize(file.size)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted mt-3 text-[11px]">Nenhum arquivo global.</p>
            )}
            {files.length > visibleFiles.length && <p className="muted mt-2 text-[10px]">+{files.length - visibleFiles.length} arquivo(s) · {formatFileSize(totalSize)}</p>}
          </div>
          <div className="rounded-xl border border-border bg-card p-3.5">
            <BlockHeading icon={History} title="Buscas globais" count={searchCount} />
            {visibleSearches.length ? (
              <ul className="mt-3 space-y-1.5">
                {visibleSearches.map((entry) => (
                  <li key={entry.id} className="flex items-center gap-2 text-xs">
                    <History className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{entry.query}</span>
                    <span className="muted shrink-0 text-[10px]">{entry.resultCount} fonte(s)</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted mt-3 text-[11px]">Nenhuma busca global registrada.</p>
            )}
            {searches.length > visibleSearches.length && <p className="muted mt-2 text-[10px]">+{searches.length - visibleSearches.length} busca(s) recente(s)</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function ContentSection({ detail: rawDetail, onRefresh }: { detail: ChatbotDetail; onRefresh: (detail: ChatbotDetail) => void }) {
  // Referência estável: os efeitos abaixo dependem de `detail.domains`; recriar
  // o objeto normalizado a cada render entraria em loop de setState.
  const detail = useMemo(() => withCollections(rawDetail), [rawDetail])
  const [domainInput, setDomainInput] = useState("")
  const [domainError, setDomainError] = useState<string>()
  const [adding, setAdding] = useState(false)
  const [showAddDomain, setShowAddDomain] = useState(detail.domains.length === 0)
  const [scope, setScope] = useState(detail.domains[0]?.id ?? GLOBAL_SCOPE)
  const [contentView, setContentView] = useState("sources")
  const [deleteTarget, setDeleteTarget] = useState<SearchDomain | null>(null)
  const [refreshingId, setRefreshingId] = useState<string>()
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string>()

  useEffect(() => { if (scope !== GLOBAL_SCOPE && !detail.domains.some(({ id }) => id === scope)) setScope(detail.domains[0]?.id ?? GLOBAL_SCOPE) }, [detail.domains, scope])

  async function addDomain() {
    if (!domainInput.trim()) return setDomainError("Digite o domínio que contém o conteúdo.")
    setAdding(true); setDomainError(undefined)
    try {
      const normalized = normalizeDomain(domainInput)
      const next = await api.addDomain(detail.id, normalized.origin)
      const addedDomain = next.domains.at(-1)
      if (addedDomain) setScope(addedDomain.id)
      setDomainInput("")
      setShowAddDomain(false)
      onRefresh(next)
      if (addedDomain) {
        try {
          const discovered = await api.discoverPages(detail.id, addedDomain.id)
          onRefresh(discovered.chatbot)
          toast.success(`${discovered.pages.length} página(s) adicionada(s) ao conhecimento.`)
        } catch {
          toast.warning("Domínio adicionado. Use 'Descobrir páginas' para tentar a descoberta novamente.")
        }
      } else toast.success("Domínio de pesquisa adicionado.")
    }
    catch (error) { setDomainError(error instanceof Error ? error.message : "Não foi possível adicionar o domínio.") }
    finally { setAdding(false) }
  }

  async function removeDomain() {
    if (!deleteTarget) return
    try { await api.deleteDomain(detail.id, deleteTarget.id); const next = await api.getChatbot(detail.id); onRefresh(next); toast.success("Domínio de pesquisa removido.") }
    catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível remover o domínio.") }
    finally { setDeleteTarget(null) }
  }

  async function refreshDomains() {
    setListBusy(true); setListError(undefined)
    try { onRefresh({ ...detail, domains: await api.listDomains(detail.id) }) }
    catch (error) { setListError(error instanceof Error ? error.message : "Não foi possível atualizar os domínios.") }
    finally { setListBusy(false) }
  }

  async function refreshDomain(domainId: string) {
    setRefreshingId(domainId)
    try {
      const domain = await api.getDomain(detail.id, domainId)
      onRefresh({ ...detail, domains: detail.domains.map((item) => item.id === domainId ? domain : item) })
      toast.success("Domínio atualizado.")
    } catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível atualizar o domínio.") }
    finally { setRefreshingId(undefined) }
  }

  async function copyDomain(domain: SearchDomain) {
    try { await navigator.clipboard.writeText(domain.origin); toast.success("Endereço copiado.") }
    catch { toast.error("Não foi possível copiar o endereço.") }
  }

  const selectedDomain = detail.domains.find((domain) => domain.id === scope)
  const totalPages = detail.domains.reduce((total, domain) => total + domain.pages.length, 0)
  const permittedPages = detail.domains.reduce((total, domain) => total + domain.pages.filter(({ selected }) => selected).length, 0)
  const totalSearches = Object.values(detail.textSearchCounts).reduce((total, count) => total + Number(count), 0)

  return (
    <div className="space-y-6">
      <section className="panel-bg overflow-hidden rounded-2xl border border-border">
        <div className="p-6 sm:p-8">
          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
            <div className="flex items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground"><Globe2 className="size-[18px]" /></span>
              <div>
                <p className="muted mb-1 text-[10px] font-semibold uppercase tracking-[.16em]">Mapa do conhecimento</p>
                <h2 className={editorHeadingClass}>Fontes conectadas</h2>
                <p className={editorDescriptionClass}>Organize sites, arquivos e buscas em um único espaço de trabalho.</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button type="button" variant="outline" className="h-10 items-center justify-center gap-2 rounded-xl border-border px-3 text-xs font-semibold" onClick={() => void refreshDomains()} disabled={listBusy}>
                {listBusy ? <LoaderCircle className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}Atualizar
              </Button>
              <Button type="button" className="accent-bg flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl px-3 text-xs font-semibold" onClick={() => setShowAddDomain((open) => !open)}>
                {showAddDomain ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
                {showAddDomain ? "Fechar" : "Adicionar domínio"}
              </Button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile label="Domínios" value={detail.domains.length} />
            <StatTile label="Páginas permitidas" value={`${permittedPages}/${totalPages}`} tone="success" />
            <StatTile label="Arquivos" value={detail.files.length} />
            <StatTile label="Buscas testadas" value={totalSearches} />
          </div>

          {showAddDomain && (
            <div className="mt-6 border-t border-border pt-5">
              <AddDomainForm id="new-search-domain" value={domainInput} onValueChange={(next) => { setDomainInput(next); setDomainError(undefined) }} onSubmit={addDomain} submitting={adding} error={domainError} placeholder="example.com" submitLabel="Adicionar domínio" />
              <p className="mt-2 text-[11px] muted">Endereços privados e de rede local não são aceitos.</p>
            </div>
          )}

          {listError && (
            <Alert variant="destructive" className="mt-5 p-3">
              <TriangleAlert className="size-3.5" />
              <AlertTitle className="text-xs font-bold">Não foi possível atualizar os domínios</AlertTitle>
              <AlertDescription className="flex flex-col gap-2 text-[11px] sm:flex-row sm:items-center sm:justify-between">
                <span>{listError}</span>
                <Button type="button" variant="outline" size="sm" className="h-7 shrink-0 rounded-md text-xs" onClick={() => void refreshDomains()}>
                  <RefreshCw className="mr-1.5 size-3" />Tentar novamente
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </section>

      <Tabs value={contentView} onValueChange={setContentView} className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl border border-border bg-card p-1">
          <TabsTrigger value="sources" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Sites e páginas</TabsTrigger>
          <TabsTrigger value="files" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Arquivos <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">{detail.files.length}</span></TabsTrigger>
          <TabsTrigger value="search" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Testar busca</TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="mt-0">
          <div className="grid items-start gap-5 lg:grid-cols-[260px_minmax(0,1fr)]" aria-busy={listBusy || undefined}>
            <aside className="panel-bg rounded-2xl border border-border p-3">
              <div className="flex items-center justify-between px-2 pb-3">
                <div>
                  <p className="text-xs font-bold">Fontes</p>
                  <p className="muted mt-0.5 text-[10px]">Selecione uma fonte para configurar</p>
                </div>
                <Badge variant="secondary" className="text-[10px] tabular-nums">{detail.domains.length + 1}</Badge>
              </div>

              <div className="space-y-1.5">
                <button type="button" onClick={() => setScope(GLOBAL_SCOPE)} aria-current={scope === GLOBAL_SCOPE ? "page" : undefined} className={cn("flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition", scope === GLOBAL_SCOPE ? "border-primary/30 bg-primary/[.06]" : "border-transparent hover:bg-muted/40")}>
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FolderOpen className="size-4" /></span>
                  <span className="min-w-0 flex-1"><span className="block truncate text-xs font-semibold">Global</span><span className="muted mt-0.5 block truncate text-[10px]">Disponível para todos os domínios</span></span>
                  <span className="muted shrink-0 text-[10px]">{detail.files.filter((file) => !file.domainId).length}</span>
                </button>

                {detail.domains.map((domain) => {
                  const permitted = domain.pages.filter(({ selected }) => selected).length
                  return (
                    <div key={domain.id} className={cn("flex items-stretch gap-1 rounded-xl border transition", scope === domain.id ? "border-primary/30 bg-primary/[.06]" : "border-transparent hover:bg-muted/40")}>
                      <button type="button" onClick={() => setScope(domain.id)} aria-current={scope === domain.id ? "page" : undefined} aria-label={`${domain.hostname}, ${domain.pages.length} páginas, ${permitted} permitidas, ${domain.files.length} arquivos`} className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-left">
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"><Globe2 className="size-4" /></span>
                        <span className="min-w-0 flex-1"><span className="block truncate text-xs font-semibold">{domain.hostname}</span><span className="muted mt-0.5 block truncate text-[10px]">{permitted}/{domain.pages.length} páginas · {domain.files.length} arquivos</span></span>
                      </button>
                      <div className="flex items-center pr-1">
                        <DomainMenu domain={domain} refreshing={refreshingId === domain.id} onRefresh={() => void refreshDomain(domain.id)} onCopy={() => void copyDomain(domain)} onRemove={() => setDeleteTarget(domain)} />
                      </div>
                    </div>
                  )
                })}
              </div>

              {!detail.domains.length && <p className="muted border-t border-border px-2 pt-3 text-[11px] leading-relaxed">Adicione um domínio para descobrir páginas e escolher o conteúdo que será rastreado.</p>}
            </aside>

            <div className="min-w-0">
              {listBusy ? (
                <div className="space-y-3">
                  <Skeleton className="h-16 w-full rounded-2xl" />
                  <Skeleton className="h-[420px] w-full rounded-2xl" />
                </div>
              ) : selectedDomain ? (
                <DomainWorkspace key={selectedDomain.id} chatbotId={detail.id} domain={selectedDomain} onRefresh={onRefresh} embedded />
              ) : (
                <GlobalBucketCard detail={detail} />
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="files" className="mt-0">
          <FileSection chatbotId={detail.id} detail={detail} domains={detail.domains} scope={scope} onScopeChange={setScope} onRefresh={onRefresh} />
        </TabsContent>

        <TabsContent value="search" className="mt-0">
          <SearchSection chatbotId={detail.id} domains={detail.domains} scope={scope} onScopeChange={setScope} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover {deleteTarget?.hostname}?</AlertDialogTitle>
            <AlertDialogDescription>As regras e páginas descobertas deste domínio também serão removidas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void removeDomain()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remover domínio</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
